<template lang="pug">
  .main-container(v-if='folderDetail')
    .row.middle.relative.m-top-3.m4-bottom-6
      .row.width-100.middle
        simple-svg.dark-gray-icon.m-right-05(:filepath='folderIcon' :width='"20px"' :height='"20px"')
        router-link.text-normal.m-right-05(:to="{ name: 'folderList', params: { rootTab: rootTab() } }")
          | {{ $t(`folders.root.${folderDetail.userPermission}`) }}
        simple-svg.dark-gray-icon.m-right-05(:filepath='rightArrowIcon' :width='"15px"' :height='"15px"')
        simple-svg.blue-icon.m-right-05(:filepath='resourceIcon' :width='"20px"' :height='"20px"')
        .text-normal.blue.bold
          | {{ folderDetail.name }}
      .row.middle.end.width-100
        folder-actions(
          v-if='folderDetail.id'
          :editPermission='!hasReadPermission'
          :folderId='folderDetail.id'
          :folderName='folderDetail.name'
        )
        button.primary-button.with-icon.m4-left-6(type='button' @click='openShareFolderModal')
          simple-svg.white-icon.m4-right-2(:filepath='shareIcon' :width='"20px"' :height='"20px"')
          | {{ $t('folders.share')}}
    .column
      krea-text-area(
        noActions
        name='name'
        :disabled='hasReadPermission'
        :value='folderName'
        :updateMethod='editFolderName'
        :maxCharacters='50'
        size='title-big'
        height='55px'
      )
      .row.middle.m4-top-3
        span.text-xsmall.dark-gray.m4-right-3
          | {{ isCopy ? $t('folders.copiedOn', { date: creationDate }) : $t('folders.createdOn', { date: creationDate }) }}
        span.text-xsmall.dark-gray.dot
          | {{ $t('folders.by', { name: foldersOwnerName }) }}
      .description.m-top-1(v-if='showDescription')
        .placeholder(v-if='!folderDetail.description && !editDescription' @click='toggleDescriptionEdition')
          h2.text-normal.dark-gray {{ $t('folders.addDescription') }}
        .description.text-small.m-bottom-1.m-top-1(v-else-if='folderDetail.description && !editDescription')
          p.text-normal.html-text(v-html='folderDetail.description' @click='toggleDescriptionEdition')
        .description-edition(v-else)
          tip-tap-editor(
            fullscreen heading orderedList preview
            unorderedList link removeLink image video quote
            height='200px'
            :value='folderDetail.description || newDescription'
            @input='editNewDescription'
          )
          .row.middle.m-top-1.end
            button.m-right-1.secondary-button(@click='toggleDescriptionEdition') {{ $t('folders.cancel') }}
            button.primary-button(@click='saveDescription') {{ $t('folders.save') }}
      .middle.order.m-top-2(@click='showResourcesModal' v-if='folderDetail.resources && folderDetail.resources.length > 1 && hasEditPermission')
        simple-svg.dark-gray-icon.m-right-1(:filepath='orderIcon' width='18px' height='18px')
        h2.dark-gray.text-normal {{ $t('folders.orderResources') }}
    .grid.grid-columns-4.gap-20.m-bottom-4(v-if='folderDetail.resources && folderDetail.resources.length')
      resource-card.card-container.m-top-4(
        v-for='resource in orderedResources'
        :key='resource.id'
        :resource='resource'
        :folderId='folderDetail.id'
        :bookmarked='!!folderDetail.id'
      )
    .row.middle.center.empty-resources(v-else)
      .column.center
        img.m-bottom-3(:src='emptyIcon' width='300px')
        h2.text-big.dark-gray {{ $t('folders.emptyResources') }}
    carousel.m-bottom-6(
      v-if='resources.length'
      :title='$t("folders.kimpleRecommends")'
    )
      slide.m-right-05(v-for='resource in resources[0].resources' :key='resource.id')
        resource-card(:resource='resource')
    remove-resource-modal(v-if='showRemoveResourceModal' :text='$t("removeResource.text")' @reload='reloadFolder')
    vue-headful(:title='viewTitle')
    choice-modal(v-if='showChoiceModal')
    folder-share-modal(v-if='showShareModal')
    create-sharing-group-modal(v-if='showCreateSharingGroupModal')
    edit-sharing-group-modal(v-if='showEditSharingGroupModal')
    add-sharing-group-members-modal(v-if='showAddSharingGroupMembersModal')
    add-resource-modal(v-if='showAddResourceModal')
    order-resources-modal(v-if='showOrderResourcesModal')
</template>

<script>
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import debounce from 'lodash.debounce'

import ResourceCard from '@/components/ResourceCard'
import Carousel from '@/components/Carousel'
import KreaTextArea from '@/components/Krea/Inputs/KreaTextArea'
import RemoveResourceModal from '@/components/RemoveResourceModal'
import AddResourceModal from '@/components/Resource/AddResource'
import FolderActions from '@/components/Folders/FolderActions'
import FolderShareModal from '@/components/Folders/FolderShareModal'
import OrderResourcesModal from '@/components/Modals/OrderResourcesModal'
import TipTapEditor from '@/components/TipTap/TipTapEditor'
import CreateSharingGroupModal from '@/components/Modals/CreateSharingGroupModal'
import EditSharingGroupModal from '@/components/Modals/EditSharingGroupModal'
import ChoiceModal from '@/components/Modals/ChoiceModal'
import AddSharingGroupMembersModal from '@/components/Modals/AddSharingGroupMembersModal'

import shareIcon from '@/assets/ic_share.svg'
import folderIcon from '@/assets/ic_folder.svg'
import emptyIcon from '@/assets/il_empty_folder_2.svg'
import resourceIcon from '@/assets/ic_resource.svg'
import rightArrowIcon from '@/assets/ic_chevron-right.svg'
import orderIcon from '@/assets/ic_order.svg'
import { myFoldersTab, sharedWithMe } from '@/constants/folders'

import { tabTitle, dateFormat, userPermissions, folderOrigins } from '../utils/constants'
import { scrollTop } from '../utils/behavior'
import { renameFolder } from '../services/FolderService'

export default {
  name: 'folderContent',
  components: {
    ChoiceModal,
    CreateSharingGroupModal,
    AddSharingGroupMembersModal,
    EditSharingGroupModal,
    KreaTextArea,
    ResourceCard,
    Carousel,
    RemoveResourceModal,
    FolderActions,
    FolderShareModal,
    AddResourceModal,
    OrderResourcesModal,
    TipTapEditor
  },
  data () {
    return {
      emptyIcon,
      showOptions: false,
      editDescription: false,
      newDescription: '',
      folderName: '',
      shareIcon,
      folderIcon,
      resourceIcon,
      rightArrowIcon,
      myFoldersTab,
      sharedWithMe,
      orderIcon
    }
  },
  computed: {
    ...mapState({
      folderDetail: state => state.folders.folderDetail,
      resources: state => state.dashboard.resources,
      showShareModal: state => state.behavior.folderShareModal.show,
      showAddResourceModal: state => state.behavior.addResourceModal.show,
      showCreateSharingGroupModal: state => state.behavior.createSharingGroupModal.show,
      showAddSharingGroupMembersModal: state => state.behavior.addSharingGroupMembersModal.show,
      showEditSharingGroupModal: state => state.behavior.editSharingGroupModal.show,
      showRemoveResourceModal: state => state.behavior.removeResourceModal.show,
      showOrderResourcesModal: state => state.behavior.orderResourcesModal.show,
      showChoiceModal: state => state.behavior.choiceModal.show
    }),
    viewTitle() {
      return this.folderDetail.name ? `${tabTitle} - ${this.folderDetail.name}` : tabTitle
    },
    creationDate() {
      return moment(this.folderDetail.createdAt).format(dateFormat)
    },
    hasReadPermission() {
      return this.folderDetail.userPermission === userPermissions.read
    },
    hasEditPermission() {
      return this.folderDetail.userPermission === userPermissions.edit || this.folderDetail.userPermission === userPermissions.owner
    },
    foldersOwnerName() {
      return this.folderDetail.userPermission === 'Owner'
        ? this.$t('folders.you')
        : this.folderDetail.ownerName
    },
    isCopy() {
      return this.folderDetail.origin === folderOrigins.copiedFromKimpleFolder
    },
    orderedResources() {
      if (!this.folderDetail.resources) return []
      return this.folderDetail.resources
        .map(res => res)
        .sort((a, b) => this.folderDetail.orderResources.indexOf(a).order - this.folderDetail.orderResources.indexOf(b).order)
    },
    showDescription() {
      return this.folderDetail.description || (!this.folderDetail.description && !this.hasReadPermission)
    }
  },
  async created() {
    await this.getFolderDetails(this.$route.params.id)
    this.newDescription = this.folderDetail.description || ''
    this.folderName = this.folderDetail.name
  },
  watch: {
    '$route' (to, from) {
      scrollTop()
    }
  },
  methods: {
    ...mapActions([
      'getFolderDetails',
      'showElement',
      'hideElement',
      'updateFolderDescription'
    ]),
    async reloadFolder() {
      await this.getFolderDetails(this.$route.params.id)
    },
    openShareFolderModal() {
      this.showElement({
        element: 'folderShareModal',
        props: { id: this.folderDetail.id, name: this.folderDetail.name, hasEditPermission: !this.hasReadPermission }
      })
    },
    rootTab() {
      return this.folderDetail.userPermission === 'Owner' ? myFoldersTab : sharedWithMe
    },
    showResourcesModal() {
      this.showElement({
        element: 'orderResourcesModal',
        props: {
          resources: this.folderDetail.resources
        }
      })
    },
    toggleDescriptionEdition() {
      if (this.hasReadPermission) return
      this.editDescription = !this.editDescription
      this.newDescription = ''
    },
    editNewDescription(value) {
      this.newDescription = value
    },
    editFolderName(value) {
      this.folderName = value
      if (this.folderName) this.saveFolderName()
    },
    saveFolderName: debounce(async function() {
      await renameFolder(this.folderDetail.id, this.folderName).catch(e => e.response)
      await this.getFolderDetails(this.$route.params.id)
    }, 1500),
    async saveDescription() {
      this.showElement({ element: 'fullPageLoader' })
      await this.updateFolderDescription({
        folderId: this.$route.params.id,
        description: this.newDescription
      })
      this.toggleDescriptionEdition()
      await this.reloadFolder()
      this.hideElement({ element: 'fullPageLoader' })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../scss/variables/sizes';
@import '../scss/variables/colors';

.empty-resources{
  padding: 120px 0;
}

.card-container {
  margin: 24px 15px 0 0;
}

.dot {
  padding-left: 16px;
  position: relative;

  &::before {
    background-color: $blue;
    border-radius: 100%;
    content: '';
    height: 5px;
    left: 0;
    position: absolute;
    top: 6px;
    width: 5px;
  }
}

.order{
  cursor: pointer;
  align-self: flex-start;
  display: inline-flex;
}

.description{
  width: 600px;
  .placeholder{
    cursor: text;
    height: 60px;
    padding: 10px;
    background-color: $light-gray;
    border-radius: 5px;
  }
}
</style>
